import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Modal } from 'react-bootstrap';
import { useProductService } from 'services/products';
import {
  UpdateProductRequest,
  Product,
  CreateProductRequest,
} from 'types/product';
import { ROUTES } from 'utils/constants';
import Button from 'components/Button';
import Toast from 'components/Toast';
import ProductHistoryModal from '../ProductHistoryModal';
import { ArrowLeft, Upload, X, Send, Copy, History } from 'lucide-react';
import styles from './EditProduct.module.scss';

type ProductFormData = {
  name: string;
  description: string;
  productCategory: string;
  price: number;
  productType: 'product' | 'service';
  trackInventory: boolean;
  isShippable: boolean;
  weight: number;
  weightUnit: string;
  length: number;
  width: number;
  height: number;
  dimensionUnit: string;
  shippingClass: string;
};

function EditProduct() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const productService = useProductService();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [isVoided, setIsVoided] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [existingMediaUrls, setExistingMediaUrls] = useState<string[]>([]);
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ProductFormData>({
    defaultValues: {
      name: '',
      description: '',
      productCategory: '',
      price: 0,
      productType: 'product',
      trackInventory: false,
      isShippable: false,
      weight: 0,
      weightUnit: 'kg',
      length: 0,
      width: 0,
      height: 0,
      dimensionUnit: 'cm',
      shippingClass: '',
    },
  });

  const productType = watch('productType');
  const isService = productType === 'service';
  const trackInventory = watch('trackInventory');
  const isShippable = watch('isShippable');

  // Update trackInventory when productType changes
  useEffect(() => {
    if (isService) {
      setValue('trackInventory', false);
    }
  }, [isService, setValue]);

  // Update showShippingDetails when isShippable changes
  useEffect(() => {
    setShowShippingDetails(isShippable);
  }, [isShippable]);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Load product data
  useEffect(() => {
    if (!id) {
      showToast('Product ID is required', 'error');
      navigate(ROUTES.PRODUCTS.ROOT);
      return;
    }

    const loadProduct = async () => {
      setLoading(true);
      try {
        const productData = await productService.getProductById(id);

        // Store product data for duplication
        setProduct(productData);

        // Check if product is a draft
        setIsDraft(productData.isDraft || false);
        // Check if product is voided
        setIsVoided(productData.isVoided || false);

        // Set existing media URLs
        if (productData.media && productData.media.length > 0) {
          setExistingMediaUrls(productData.media);
        }

        // Set shipping details visibility
        setShowShippingDetails(productData.isShippable);

        // Reset form with product data
        reset({
          name: productData.name,
          description: productData.description || '',
          productCategory: productData.productCategory || '',
          price: Number(productData.price),
          productType: productData.trackInventory ? 'product' : 'service',
          trackInventory: productData.trackInventory,
          isShippable: productData.isShippable,
          weight: productData.shippingDetails?.weight || 0,
          weightUnit: productData.shippingDetails?.weightUnit || 'kg',
          length: productData.shippingDetails?.length || 0,
          width: productData.shippingDetails?.width || 0,
          height: productData.shippingDetails?.height || 0,
          dimensionUnit: productData.shippingDetails?.dimensionUnit || 'cm',
          shippingClass: productData.shippingDetails?.shippingClass || '',
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to load product. Please try again.';
        showToast(errorMessage, 'error');
        navigate(ROUTES.PRODUCTS.ROOT);
      } finally {
        setLoading(false);
      }
    };

    void loadProduct();
  }, [id, productService, reset, showToast, navigate]);

  const handleMediaChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      const newFiles = [...mediaFiles, ...files];
      setMediaFiles(newFiles);

      // Create previews
      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setMediaPreviews([...mediaPreviews, ...newPreviews]);
    },
    [mediaFiles, mediaPreviews],
  );

  const handleRemoveMedia = useCallback(
    (index: number) => {
      const newFiles = mediaFiles.filter((_, i) => i !== index);
      const newPreviews = mediaPreviews.filter((_, i) => i !== index);
      setMediaFiles(newFiles);
      setMediaPreviews(newPreviews);
      // Revoke object URLs to prevent memory leaks
      URL.revokeObjectURL(mediaPreviews[index]);
    },
    [mediaFiles, mediaPreviews],
  );

  const handleRemoveExistingMedia = useCallback((index: number) => {
    setExistingMediaUrls((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const onSubmit: SubmitHandler<ProductFormData> = async (data) => {
    if (!id) {
      showToast('Product ID is required', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      // Combine existing media URLs with new file names
      // TODO: Implement actual file upload when backend is ready
      const newMediaUrls = mediaFiles.map((file) => file.name);
      const allMediaUrls = [...existingMediaUrls, ...newMediaUrls];

      // If productType is service, trackInventory should be false
      const shouldTrackInventory =
        data.trackInventory && data.productType === 'product';

      const productData: UpdateProductRequest = {
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        productCategory: data.productCategory.trim() || undefined,
        price: Number(data.price),
        // trackInventory is immutable after creation, only include for drafts
        ...(isDraft && { trackInventory: shouldTrackInventory }),
        isShippable: data.isShippable,
        shippingDetails: data.isShippable
          ? {
              weight: data.weight > 0 ? Number(data.weight) : undefined,
              weightUnit: data.weightUnit || undefined,
              length: data.length > 0 ? Number(data.length) : undefined,
              width: data.width > 0 ? Number(data.width) : undefined,
              height: data.height > 0 ? Number(data.height) : undefined,
              dimensionUnit: data.dimensionUnit || undefined,
              shippingClass: data.shippingClass.trim() || undefined,
            }
          : undefined,
        media: allMediaUrls.length > 0 ? allMediaUrls : undefined,
      };

      // Use updateDraftProduct if it's a draft, otherwise use updateProduct
      if (isDraft) {
        await productService.updateDraftProduct(id, productData);
        showToast('Draft updated successfully!', 'success');
      } else {
        await productService.updateProduct(id, productData);
        showToast('Product updated successfully!', 'success');
      }
      setTimeout(() => {
        navigate(ROUTES.PRODUCTS.ROOT);
      }, 1000);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update product. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicate = useCallback(() => {
    setShowDuplicateModal(true);
  }, []);

  const handleDuplicateCancel = useCallback(() => {
    setShowDuplicateModal(false);
  }, []);

  const handleDuplicateConfirm = useCallback(async () => {
    if (!product) {
      showToast('Product data is not available', 'error');
      return;
    }

    setIsSubmitting(true);
    setShowDuplicateModal(false);

    try {
      // Create duplicate product as draft
      const duplicateData: CreateProductRequest = {
        name: `${product.name} (Copy)`,
        description: product.description,
        media: product.media,
        productCategory: product.productCategory,
        price: Number(product.price),
        trackInventory: product.trackInventory,
        isShippable: product.isShippable,
        shippingDetails: product.shippingDetails,
        // Note: initialStock is not included as we're creating a draft
      };

      const duplicatedProduct =
        await productService.saveDraftProduct(duplicateData);
      showToast('Product duplicated successfully!', 'success');

      // Navigate to edit page for the new draft
      setTimeout(() => {
        navigate(ROUTES.PRODUCTS.EDIT.replace(':id', duplicatedProduct.id));
      }, 1000);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to duplicate product. Please try again.';
      showToast(errorMessage, 'error');
      setIsSubmitting(false);
    }
  }, [product, productService, showToast, navigate]);

  const handlePublish = useCallback(async () => {
    if (!id) {
      showToast('Product ID is required', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await productService.publishDraftProduct(id);
      showToast('Draft published successfully!', 'success');
      setTimeout(() => {
        navigate(ROUTES.PRODUCTS.ROOT);
      }, 1000);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to publish draft. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [id, productService, showToast, navigate]);

  const handleHistoryRollbackSuccess = useCallback(async () => {
    // Reload product data after rollback
    if (!id) return;
    try {
      const productData = await productService.getProductById(id);
      setProduct(productData);
      setIsDraft(productData.isDraft || false);
      setIsVoided(productData.isVoided || false);
      if (productData.media && productData.media.length > 0) {
        setExistingMediaUrls(productData.media);
      }
      setShowShippingDetails(productData.isShippable);
      reset({
        name: productData.name,
        description: productData.description || '',
        productCategory: productData.productCategory || '',
        price: Number(productData.price),
        productType: productData.trackInventory ? 'product' : 'service',
        trackInventory: productData.trackInventory,
        isShippable: productData.isShippable,
        weight: productData.shippingDetails?.weight || 0,
        weightUnit: productData.shippingDetails?.weightUnit || 'kg',
        length: productData.shippingDetails?.length || 0,
        width: productData.shippingDetails?.width || 0,
        height: productData.shippingDetails?.height || 0,
        dimensionUnit: productData.shippingDetails?.dimensionUnit || 'cm',
        shippingClass: productData.shippingDetails?.shippingClass || '',
      });
    } catch (error) {
      console.error('Failed to reload product after rollback:', error);
    }
  }, [id, productService, reset]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading product...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className={styles.header}>
        <div className={styles.headerContent}>
          <button
            onClick={() => navigate(ROUTES.PRODUCTS.ROOT)}
            className={styles.backButton}
            aria-label="Back to products"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className={styles.title}>Edit Product</h1>
        </div>
        {!isDraft && id && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistoryModal(true)}
            className={styles.historyButton}
            aria-label="View product history"
            disabled={!product?.hasHistory}
          >
            <History size={16} />
            View History
          </Button>
        )}
      </div>

      <form
        onSubmit={(e) => {
          if (isVoided) {
            e.preventDefault();
            return;
          }
          void handleSubmit(onSubmit)(e);
        }}
        className={styles.form}
      >
        {/* Basic Information */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Basic Information</h2>

          <div className={styles.formGroup}>
            <label htmlFor="name" className={styles.label}>
              Product Name <span className={styles.required}>*</span>
            </label>
            <input
              id="name"
              type="text"
              className={`${styles.textInput} ${
                errors.name ? styles.errorInput : ''
              }`}
              {...register('name', {
                required: 'Product name is required',
                minLength: {
                  value: 1,
                  message: 'Product name must be at least 1 character',
                },
              })}
              placeholder="Enter product name"
              disabled={isVoided}
            />
            {errors.name && (
              <p className={styles.errorMessage}>{errors.name.message}</p>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description" className={styles.label}>
              Description
            </label>
            <textarea
              id="description"
              className={styles.textarea}
              {...register('description')}
              placeholder="Enter product description"
              rows={4}
              disabled={isVoided}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="productCategory" className={styles.label}>
              Product Category
            </label>
            <input
              id="productCategory"
              type="text"
              className={styles.textInput}
              {...register('productCategory')}
              placeholder="Enter category (optional)"
              disabled={isVoided}
            />
            <p className={styles.helperText}>
              Group products by category for better organization
            </p>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="price" className={styles.label}>
              Price <span className={styles.required}>*</span>
            </label>
            <input
              id="price"
              type="number"
              step="0.01"
              min="0"
              className={`${styles.numberInput} ${
                errors.price ? styles.errorInput : ''
              }`}
              {...register('price', {
                required: 'Price is required',
                min: {
                  value: 0,
                  message: 'Price must be greater than or equal to 0',
                },
              })}
              placeholder="0.00"
              disabled={isVoided}
            />
            {errors.price && (
              <p className={styles.errorMessage}>{errors.price.message}</p>
            )}
          </div>
        </div>

        {/* Media */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Media</h2>
          <div className={styles.formGroup}>
            <label htmlFor="media" className={styles.label}>
              Product Images
            </label>
            <div className={styles.mediaUpload}>
              <input
                id="media"
                type="file"
                accept="image/*"
                multiple
                onChange={handleMediaChange}
                className={styles.fileInput}
                disabled={isVoided}
              />
              <label htmlFor="media" className={styles.uploadButton}>
                <Upload size={18} />
                Upload Images
              </label>
            </div>
            {(existingMediaUrls.length > 0 || mediaPreviews.length > 0) && (
              <div className={styles.mediaPreviews}>
                {existingMediaUrls.map((url, index) => (
                  <div
                    key={`existing-${index}`}
                    className={styles.mediaPreview}
                  >
                    <img src={url} alt={`Product ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingMedia(index)}
                      className={styles.removeMediaButton}
                      aria-label="Remove image"
                      disabled={isVoided}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                {mediaPreviews.map((preview, index) => (
                  <div key={`new-${index}`} className={styles.mediaPreview}>
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => handleRemoveMedia(index)}
                      className={styles.removeMediaButton}
                      aria-label="Remove image"
                      disabled={isVoided}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Product Type */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Product Type</h2>
          <div className={styles.formGroup}>
            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value="product"
                  {...register('productType')}
                  className={styles.radio}
                  disabled={isVoided || !isDraft}
                />
                <span>Product</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  value="service"
                  {...register('productType')}
                  className={styles.radio}
                  disabled={isVoided || !isDraft}
                />
                <span>Service</span>
              </label>
            </div>
            <p className={styles.helperText}>
              {isDraft
                ? "Services don't require inventory tracking"
                : 'Product type cannot be changed after creation'}
            </p>
          </div>
        </div>

        {/* Inventory */}
        {!isService && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Inventory</h2>
            {isDraft ? (
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    {...register('trackInventory')}
                    className={styles.checkbox}
                    disabled={isVoided}
                  />
                  <span>Track inventory for this product</span>
                </label>
              </div>
            ) : (
              <div className={styles.formGroup}>
                <p className={styles.helperText}>
                  {trackInventory
                    ? 'This product tracks inventory. Inventory tracking cannot be changed after creation.'
                    : 'This product does not track inventory. Inventory tracking cannot be changed after creation.'}
                </p>
              </div>
            )}

            {trackInventory && (
              <>
                <div className={styles.formGroup}>
                  <div className={styles.infoBox}>
                    <p className={styles.infoText}>
                      To adjust stock quantities, view inventory history, or
                      make inventory changes, visit the{' '}
                      <button
                        type="button"
                        onClick={() => {
                          navigate(
                            ROUTES.PRODUCTS.INVENTORY.replace(':id', id || ''),
                          );
                        }}
                        className={styles.infoLink}
                      >
                        dedicated inventory management page
                      </button>
                      .
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Shipping */}
        {!isService && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Shipping</h2>
            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  {...register('isShippable')}
                  className={styles.checkbox}
                  disabled={isVoided}
                />
                <span>This product requires shipping</span>
              </label>
            </div>

            {showShippingDetails && (
              <div className={styles.shippingDetails}>
                <div className={styles.formGroup}>
                  <label htmlFor="weight" className={styles.label}>
                    Weight (kg)
                  </label>
                  <input
                    id="weight"
                    type="number"
                    step="0.01"
                    min="0"
                    className={styles.numberInput}
                    {...register('weight', {
                      min: {
                        value: 0,
                        message: 'Weight must be greater than or equal to 0',
                      },
                    })}
                    placeholder="0.00"
                    disabled={isVoided}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Dimensions (cm)</label>
                  <div className={styles.dimensionsGrid}>
                    <div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={styles.numberInput}
                        {...register('length', {
                          min: {
                            value: 0,
                            message:
                              'Length must be greater than or equal to 0',
                          },
                        })}
                        placeholder="Length"
                        disabled={isVoided}
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={styles.numberInput}
                        {...register('width', {
                          min: {
                            value: 0,
                            message: 'Width must be greater than or equal to 0',
                          },
                        })}
                        placeholder="Width"
                        disabled={isVoided}
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={styles.numberInput}
                        {...register('height', {
                          min: {
                            value: 0,
                            message:
                              'Height must be greater than or equal to 0',
                          },
                        })}
                        placeholder="Height"
                        disabled={isVoided}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="shippingClass" className={styles.label}>
                    Shipping Class
                  </label>
                  <input
                    id="shippingClass"
                    type="text"
                    className={styles.textInput}
                    {...register('shippingClass')}
                    placeholder="e.g., Standard, Express, Fragile"
                    disabled={isVoided}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Form Actions */}
        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(ROUTES.PRODUCTS.ROOT)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {isVoided ? (
            <Button
              type="button"
              variant="primary"
              onClick={handleDuplicate}
              disabled={isSubmitting}
            >
              <Copy size={18} style={{ marginRight: '0.5rem' }} />
              Duplicate Product
            </Button>
          ) : (
            <>
              {isDraft && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    void handlePublish();
                  }}
                  disabled={isSubmitting}
                >
                  <Send size={18} style={{ marginRight: '0.5rem' }} />
                  {isSubmitting ? 'Publishing...' : 'Publish Draft'}
                </Button>
              )}
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? isDraft
                    ? 'Updating Draft...'
                    : 'Updating...'
                  : isDraft
                    ? 'Update Draft'
                    : 'Update Product'}
              </Button>
            </>
          )}
        </div>
      </form>

      {/* Duplicate Confirmation Modal */}
      <Modal
        show={showDuplicateModal}
        onHide={handleDuplicateCancel}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Duplicate Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to duplicate{' '}
            <strong>"{product?.name}"</strong>?
          </p>
          <p className={styles.modalWarning}>
            This will create a new draft product with the same information. You
            can edit and publish it later.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDuplicateCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleDuplicateConfirm();
            }}
            disabled={isSubmitting}
          >
            <Copy size={18} style={{ marginRight: '0.5rem' }} />
            {isSubmitting ? 'Duplicating...' : 'Duplicate Product'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Product History Modal */}
      {id && (
        <ProductHistoryModal
          show={showHistoryModal}
          onHide={() => setShowHistoryModal(false)}
          productId={id}
          onRollbackSuccess={() => void handleHistoryRollbackSuccess()}
          onError={(message) => showToast(message, 'error')}
        />
      )}
    </div>
  );
}

export default EditProduct;
